import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface NoteAttachment {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  url: string
}

export interface ConversationNote {
  id: string
  conversationId: string
  authorAgentId: string | null
  authorName: string | null
  body: string
  bodyHtml: string | null
  isPrivate: boolean
  attachments: NoteAttachment[]
  createdAt: string
  updatedAt: string
}

interface NotesResponse {
  notes: ConversationNote[]
}

interface CreateNotePayload {
  body: string
  bodyHtml?: string
  isPrivate?: boolean
  attachments?: string[]
}

interface CreateNoteResponse {
  note: ConversationNote
}

interface DeleteNoteResponse {
  deleted: boolean
}

export interface UseConversationNotesResult {
  notes: ConversationNote[]
  loading: boolean
  error: string | null
  creating: boolean
  refetch: () => Promise<void>
  createNote: (payload: CreateNotePayload) => Promise<ConversationNote | null>
  deleteNote: (noteId: string) => Promise<boolean>
}

export function useConversationNotes(conversationId: string | null): UseConversationNotesResult {
  const [notes, setNotes] = useState<ConversationNote[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const refetch = useCallback(async () => {
    if (!conversationId) {
      setNotes([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<NotesResponse>(`/conversations/${conversationId}/notes`)
      setNotes(res.notes)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes')
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const createNote = useCallback(
    async (payload: CreateNotePayload): Promise<ConversationNote | null> => {
      if (!conversationId) return null
      setCreating(true)
      try {
        const res = await api.post<CreateNoteResponse>(
          `/conversations/${conversationId}/notes`,
          payload,
        )
        setNotes((prev) => [res.note, ...prev])
        return res.note
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create note')
        return null
      } finally {
        setCreating(false)
      }
    },
    [conversationId],
  )

  const deleteNote = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!conversationId) return false
      try {
        const res = await api.delete<DeleteNoteResponse>(
          `/conversations/${conversationId}/notes/${noteId}`,
        )
        if (res.deleted) {
          setNotes((prev) => prev.filter((n) => n.id !== noteId))
        }
        return res.deleted
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete note')
        return false
      }
    },
    [conversationId],
  )

  return { notes, loading, error, creating, refetch, createNote, deleteNote }
}
